import type { RouteObject } from "react-router-dom";
import { redirect } from "react-router-dom";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";
import { Spinner } from "@/components/ui/v2/spinner";
import {
  CompanySalesCommissions,
  CompanyAllSalesPage,
  CompanySaleRequestsPage,
  CompanyCommissionsPage,
  CompanySaleCreate,
  CompanySaleDetail,
  CompanySalesCommissionsSettingsPage,
} from "@/utils/lazyLoadComponents";

export enum COMPANY_SALES_PATH_SEGMENTS {
  ROOT = "sales-commissions",
  ALL = "all",
  REQUESTS = "requests",
  COMMISSIONS = "commissions",
  CREATE = "create",
  DETAILS = ":id",
  // Module settings (commission plans). Literal, not `MODULE_SETTINGS_SEGMENT`:
  // `isolatedModules` forbids enum members initialized from an imported const.
  // Keep both in sync.
  SETTINGS = "settings",
}

export const companySalesCommissionsRoutes: RouteObject = {
  path: COMPANY_SALES_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Sales & Commissions" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout: each tab owns its own stats + table ──────────
    {
      Component: CompanySalesCommissions,
      handle: { flushContent: true },
      children: [
        {
          index: true,
          loader: () => redirect(COMPANY_SALES_PATH_SEGMENTS.ALL),
        },
        {
          path: COMPANY_SALES_PATH_SEGMENTS.ALL,
          handle: { breadcrumb: "All Sales" },
          Component: CompanyAllSalesPage,
        },
        {
          path: COMPANY_SALES_PATH_SEGMENTS.REQUESTS,
          handle: { breadcrumb: "Sale Approvals" },
          Component: CompanySaleRequestsPage,
        },
        {
          path: COMPANY_SALES_PATH_SEGMENTS.COMMISSIONS,
          handle: { breadcrumb: "Commissions" },
          Component: CompanyCommissionsPage,
        },
      ],
    },
    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab.
    // Static "settings" also outranks the dynamic ":id" sibling in React
    // Router's route ranking, so there is no collision. ─────────────────────
    {
      path: COMPANY_SALES_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Sales & Commissions Settings" },
      Component: CompanySalesCommissionsSettingsPage,
    },
    {
      path: COMPANY_SALES_PATH_SEGMENTS.CREATE,
      handle: { breadcrumb: "Log a Sale" },
      Component: CompanySaleCreate,
    },
    {
      path: COMPANY_SALES_PATH_SEGMENTS.DETAILS,
      handle: { breadcrumb: "Sale Details" },
      Component: CompanySaleDetail,
    },
  ],
};

export const COMPANY_SALES_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_SALES_PATH_SEGMENTS.ROOT)),
  ALL: withCompanyPrefix(
    addSlashes(
      COMPANY_SALES_PATH_SEGMENTS.ROOT,
      COMPANY_SALES_PATH_SEGMENTS.ALL,
    ),
  ),
  REQUESTS: withCompanyPrefix(
    addSlashes(
      COMPANY_SALES_PATH_SEGMENTS.ROOT,
      COMPANY_SALES_PATH_SEGMENTS.REQUESTS,
    ),
  ),
  COMMISSIONS: withCompanyPrefix(
    addSlashes(
      COMPANY_SALES_PATH_SEGMENTS.ROOT,
      COMPANY_SALES_PATH_SEGMENTS.COMMISSIONS,
    ),
  ),
  CREATE: withCompanyPrefix(
    addSlashes(
      COMPANY_SALES_PATH_SEGMENTS.ROOT,
      COMPANY_SALES_PATH_SEGMENTS.CREATE,
    ),
  ),

  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.SALES_COMMISSIONS.ROOT,
  SETTINGS_VTAB_PLANS: MODULE_SETTINGS_PATHS.SALES_COMMISSIONS.VTAB_PLANS,

  DETAIL: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_SALES_PATH_SEGMENTS.ROOT, String(id)),
    ),
});
